import { CLASSIFICATION_CUBE_NAME, HCP_CUBE_NAME } from '@/api/hcp/hcp/constants';
import { isNonEmptyString } from '@/api/queries/utils';
import { GetOptionsQueryFunction } from '@/types/cube';
import { FilterConfigDynamic } from '@/types/filter';
import { toStringOrNull } from '@/utils/cube';
import { getCategoryRank, getTaArchetypeRank } from '@/utils/segmentation/taArchetypeOrder';

const COMPOSITE_SEPARATOR = '|||';

const parseCompositeValue = (composite: string): { productName: string; label: string } => {
  const separatorIndex = composite.indexOf(COMPOSITE_SEPARATOR);
  if (separatorIndex === -1) return { productName: '', label: composite };
  return {
    productName: composite.slice(0, separatorIndex),
    label: composite.slice(separatorIndex + COMPOSITE_SEPARATOR.length),
  };
};

const TA_ARCHETYPE_OPTIONS: GetOptionsQueryFunction = ({ sourceCube }) => ({
  dimensions: [
    `${sourceCube}.ta_archetype_label`,
    `${sourceCube}.ta_archetype_core_product_name`,
    `${sourceCube}.ta_archetype`,
  ],
  limit: undefined,
});

const sourceCube = CLASSIFICATION_CUBE_NAME;
const LABEL_KEY = `${sourceCube}.ta_archetype_label`;
const PRODUCT_NAME_KEY = `${sourceCube}.ta_archetype_core_product_name`;
const ARCHETYPE_KEY = `${sourceCube}.ta_archetype`;

const ophthalmologyPersonaFilter: FilterConfigDynamic = {
  label: 'global.filters.formControls.ophthalmologyPersonaLabel',
  sourceCube,
  defaultValue: [],
  getOptionsQuery: TA_ARCHETYPE_OPTIONS,
  mapResult: result => {
    if (!result) return { value: '', label: null, category: null };
    const label = toStringOrNull(result[LABEL_KEY]);
    const productName = toStringOrNull(result[PRODUCT_NAME_KEY]);
    const archetype = toStringOrNull(result[ARCHETYPE_KEY]);
    return {
      value: `${productName ?? ''}${COMPOSITE_SEPARATOR}${label ?? ''}`,
      label,
      category: productName,
      categoryOrder: getCategoryRank(productName),
      sortIndex: getTaArchetypeRank(archetype),
    };
  },
  // Orders options globally by archetype rank; the within-group archetype order (1->4)
  // only holds because the shared MultiSelect regroups categories with a STABLE sort.
  sort: options => [...options].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0)),
  type: 'multiselect',
  clearable: true,
  placeholder: 'global.filters.formControls.ophthalmologyPersonaPlaceholder',
  searchable: true,
  format: ({ value }) => {
    if (!isNonEmptyString(value)) return '';
    return parseCompositeValue(value).label;
  },
  applyFilter: value => {
    const composites = value as string[];
    const byProduct = new Map<string, string[]>();
    for (const composite of composites) {
      const { productName, label } = parseCompositeValue(composite);
      const existing = byProduct.get(productName);
      if (existing) existing.push(label);
      else byProduct.set(productName, [label]);
    }
    const groups = [...byProduct.entries()].map(([productName, labels]) => ({
      and: [
        {
          member: `${sourceCube}.ta_archetype_label`,
          operator: 'equals' as const,
          values: labels,
        },
        {
          member: `${sourceCube}.ta_archetype_core_product_name`,
          operator: 'equals' as const,
          values: [productName],
        },
      ],
    }));

    const archetypeFilter = groups.length === 1 ? groups[0] : { or: groups };
    return {
      and: [archetypeFilter, { member: `${HCP_CUBE_NAME}.hcp_bk`, operator: 'set' }],
    };
  },
};

export default ophthalmologyPersonaFilter;
